import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import { observer } from 'mobx-react-lite';

import {
    Sizes,
    Colours,
    StarRating,
    PageBehaviourButton,
    getFormattedNumber,
    getDesignationMessage,
} from '@ratehub/base-ui';

import CreditCardShape from '../definitions/CreditCardShape';
import CreditCardCategoryPill from './CreditCardCategoryPill';


function CardLayoutTitleArea({
    name,
    ratehubReview,
    displayType,
    bestUsedFor,
    isSponsored,
    isMonetized,
    onClick,
    className,
    isAWidget = false,
    isEqualizeHeight = false,
}) {
    const intl = useIntl();

    const isStarRatingDefined = typeof ratehubReview?.rating === 'number';
    const starRating = ratehubReview?.rating ?? (isEqualizeHeight
        ? 0              // ensures starRating is numeric if undefined but isEqualizeHeight is true
        : undefined);    // without isEqualizeHeight, undefined ratings are not displayed

    return (
        <div className={className}>
            <If condition={!isEqualizeHeight && (isSponsored || isMonetized)}>
                <span
                    className="rh-text-xs rh-text-lowercase"
                    data-test-name="designation-message"
                >
                    {getDesignationMessage({ isSponsored, isMonetized })}
                </span>
            </If>

            <div className="name-wrapper">
                <h3
                    className={classNames('rh-mr-0_5', 'rh-mb-0', {
                        'rh-mt-0': isEqualizeHeight,
                        'rh-mt-0_75': !isEqualizeHeight,
                    })}
                >
                    <PageBehaviourButton
                        className={classNames('card-name-link rh-title-s rh-m-0 weight-regular rh-text-align-left',
                            'rh-fg-blackberry rh-text-wrap-pretty', {
                                isEqualizeHeight: isEqualizeHeight,
                            })
                        }
                        onClick={onClick}
                        target="blank"
                        rel="noopener"
                        message={name}
                        shouldLowerCaseMessage={false}

                        data-name="card-title-text"
                    />
                </h3>

                <div className="rh-display-flex rh-flex-wrap rh-align-items-center rh-mt-0_25 rh-mb-0_75">
                    <If condition={!isAWidget}>
                        <If condition={isStarRatingDefined || isEqualizeHeight}>
                            <span className="star-rating">
                                <StarRating
                                    ratingPercent={starRating * 20}
                                    starSize={Sizes.ICONS['XS']}
                                />
                                <span className="rating rh-position-relative rh-text-xs rh-mr-2 rh-ml-0_5">
                                    <FormattedMessage
                                        {...isStarRatingDefined
                                            ? LOCAL_MESSAGES.ratehubRated
                                            : LOCAL_MESSAGES.notRated
                                        }
                                        values={{ rating: getFormattedNumber(starRating, 'decimal1', intl) }}
                                    />
                                </span>
                            </span>
                        </If>

                        <If condition={displayType}>
                            <CreditCardCategoryPill
                                className="rh-mt-0_25"
                                category={displayType}
                            />
                        </If>

                        {/* we do not expect both pills to be requested (and they do visually clash),
                            but if they should, we favour the category pill above, which is based on displayType */}
                        <If condition={bestUsedFor && !displayType}>
                            <h4 className={classNames('best-for rh-text-xs weight-regular rh-bg-blueberry-lightest',
                                'rh-px-0_5 rh-py-0_25 rh-mt-0_25 rh-mr-0_5 rh-mb-0 rh-ml-0 rh-border-radius-4px')}
                            >
                                <FormattedMessage
                                    id="edb.credit-card.best-for"
                                    defaultMessage="Best for {bestUsedFor}"
                                    values={{ bestUsedFor }}
                                />
                            </h4>
                        </If>
                    </If>
                </div>
            </div>
        </div>
    );
}

CardLayoutTitleArea.propTypes = {
    name: PropTypes.string.isRequired,
    ratehubReview: CreditCardShape.ratehubReview,
    displayType: PropTypes.string,
    bestUsedFor: PropTypes.string,
    isSponsored: PropTypes.bool.isRequired,
    isMonetized: PropTypes.bool.isRequired,
    onClick: PropTypes.func,

    className: PropTypes.string,
    isAWidget: PropTypes.bool,
    isEqualizeHeight: PropTypes.bool,
};


const LOCAL_MESSAGES = defineMessages({
    ratehubRated: {
        id: 'edb.credit-card.ratehub-rated',
        defaultMessage: '{rating} Ratehub rated',
    },
    notRated: {
        id: 'edb.credit-card.not-rated',
        defaultMessage: 'not rated',
    },
});


export default styled(observer(CardLayoutTitleArea))`
    .card-name-link {
        &.isEqualizeHeight {
            min-height: 3.3em;
        }

        &:hover {
            text-decoration: underline;
            color: ${Colours.BLACKBERRY};
        }
    }

    .star-rating {
        > .rating {
            bottom: 3px;
        }
    }
`;
